/* IBM Confidential
 *
 * OCO Source Materials
 *
 * IBM Cognos Products: ba-ui-toolkit
 * (C) Copyright IBM Corp. 2019, 2020
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U.S. Copyright Office.
 * NLS_ENCODING=UTF-8
 * NLS_MESSAGEFORMAT_NONE
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { classnames as cx, propsUtils, delayMount } from '@ba-ui-toolkit/utils';
import { Loading, InlineLoading } from 'carbon';

import Tooltip from '../tooltip/Tooltip.js';
import './ProgressIndicator.scss';

const { BaPropTypes, sanitize } = propsUtils;

export const DEFAULT_DELAY = 250;
class ProgressIndicator extends Component {
	static propTypes = {
		/** If the element can be focused witht the tab key */
		tabIndex: PropTypes.oneOf([-1, 0]),
		/** Choose from fixed sizes */
		variant: PropTypes.oneOf(['circle', 'bar', 'inline']),
		/** Choose from fixed sizes */
		size: PropTypes.oneOf(['small', 'normal', 'large']),
		/** ( Temporarily deprecated ) Used when determinate is true to indicate progress stage */
		progress: BaPropTypes.range(0, 100),
		/** Specify an description that would be used to best describe the loading state */
		description: PropTypes.string,
		/** Custom class name(s) */
		className: PropTypes.string,
		/** Custom CSS styles */
		style: PropTypes.object,
		/** ( Temporarily deprecated ) Display progress updates */
		determinate: PropTypes.bool,
		/** Specify whether you want the loading indicator to be spinning or not */
		active: PropTypes.bool,
		/** Specify whether you want the loader to be applied with an overlay */
		withOverlay: PropTypes.bool,
		/** Additional props (status, iconDescription, onSuccess) for inline varirant */
		inlineOptions: PropTypes.shape({
			status: PropTypes.oneOf(['inactive', 'active', 'finished', 'error']),
			iconDescription: PropTypes.string,
			/** Optional callback function to be invoked when complete  */
			onSuccess: PropTypes.func,
		}),
	};

	static defaultProps = {
		tabIndex: -1,
		size: 'normal',
		determinate: false,
		variant: 'circle',
		description: '',
		className: '',
		style: {},
		active: true,
		withOverlay: false,
	};

	_isDeterminate() {
		return this.props.progress != null;
	}

	_getProgressProps() {
		const { tabIndex, className, style, ...others } = this.props;

		const props = {
			...sanitize(others, ProgressIndicator),
			tabIndex: tabIndex === 0 ? 0 : null,
			role: 'progressbar',
			className,
			style,
		};

		return props;
	}

	_barRender() {
		const { variant, className } = this.props;

		const classNames = cx(className, variant);

		return (
			<Tooltip
				id="tooltip-bottom"
				title={this._isDeterminate() ? `${this.props.progress}%` : 'indeterminate'}
				disabled={!this._isDeterminate()}
				orient="bottom"
				style={{ width: '100%' }}
			>
				<div {...this._getProgressProps()} className={classNames}>
					<div className="ba-common-progressIndicator__determinateFiller" />
				</div>
			</Tooltip>
		);
	}

	_inlineRender = () => {
		const { status, iconDescription, onSuccess } = this.props.inlineOptions;

		if (this.context.pureCarbon) {
			return <InlineLoading {...this.props} />;
		}

		const inlineProps = {
			status,
			description: this.props.description,
			iconDescription,
			onSuccess,
		};

		return <InlineLoading {...this._getProgressProps} {...inlineProps} />;
	};

	_circleRender = () => {
		const { size, withOverlay, active, description } = this.props;

		const circleProps = {
			small: size === 'small' || size === 'normal',
			withOverlay,
			active,
			description,
		};

		return <Loading {...this._getProgressProps} {...circleProps} />;
	};

	render() {
		const { variant } = this.props;

		return this[`_${variant}Render`]();
	}
}

export default delayMount(DEFAULT_DELAY)(ProgressIndicator);
